import { handleAxiosError } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { client } from '@/client';
import { ExportType } from '@/client/reports';
import { SystemRoles } from '@/client/users';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { AxiosError } from 'axios';
import { Field, Form, Formik } from 'formik';
import { Dialog, DialogProps } from 'primereact/dialog';
import { RadioButtonChangeEvent } from 'primereact/radiobutton';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { FormikRadio } from '../form';
import { ExportContext } from '../reports/ReportMessage';

const StyledDialog = styled(Dialog)`
  width: 500px;
`;

type AccountsExportModalProps = {
  defaultFilters: FiltersType | undefined;
  filters: FiltersType | undefined;
  companyOwnerName?: string;
} & DialogProps;

export const AccountsExportModal: React.FC<AccountsExportModalProps> = ({
  defaultFilters,
  filters,
  companyOwnerName,
  onHide,
  visible,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);

  const canExport = [SystemRoles.SUPER_ADMIN, SystemRoles.DEVELOPER].includes(
    currentUser?.role?.code as SystemRoles,
  );

  const {
    setType,
    setExportKey,
    setIsDownloading,
    setNotificationParam,
    isConnected,
  } = useContext(ExportContext);

  const exportAccounts = async ({ isAllData }: { isAllData: boolean }) => {
    if (!account || !canExport) return;
    onHide();
    setIsDownloading(true);
    setExportKey(undefined);
    setType(ExportType.ACCOUNTS);
    setNotificationParam(account.id);

    await new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (isConnected) {
          clearInterval(intervalId);
          resolve(true);
        }
      }, 100);
    });

    try {
      if (isConnected) {
        const accountsExportKey = v4();
        setExportKey(accountsExportKey);

        client.accounts.exportAccounts({
          exportKey: accountsExportKey,
          filters: isAllData
            ? defaultFilters
            : [...(defaultFilters ?? []), ...(filters ?? [])],
          companyOwnerName: isAllData ? undefined : companyOwnerName,
        });
      }
    } catch (e) {
      setIsDownloading(false);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <StyledDialog
      blockScroll
      visible={visible}
      header={<h1>{t('generic.export')}</h1>}
      onHide={onHide}
      draggable={false}
    >
      <Formik initialValues={{ isAllData: true }} onSubmit={exportAccounts}>
        {({ setFieldValue, values }) => (
          <Form>
            <label htmlFor="data">{t('reports.export.data')}</label>
            <FlexContainer
              id="data"
              gap={24}
              justify="flex-start"
              className="mt-1 mb-4 w-8"
            >
              <div className="field-radio flex-1" data-id="allData">
                <Field
                  id="allData"
                  type="radio"
                  name="allData"
                  value={true}
                  label={t('reports.export.allData')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('isAllData', e.value)
                  }
                  checked={!!values.isAllData}
                  component={FormikRadio}
                />
              </div>
              <div className="field-radio flex-1" data-id="filteredData">
                <Field
                  id="filteredData"
                  type="radio"
                  name="filteredData"
                  value={false}
                  label={t('reports.export.filteredData')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('isAllData', e.value)
                  }
                  checked={!values.isAllData}
                  component={FormikRadio}
                />
              </div>
            </FlexContainer>

            <FlexContainer justify="flex-end" className="mt-5">
              <AppButton
                label={t('button.cancel')}
                severity="secondary"
                type="outlined"
                onClick={onHide}
                className="mr-3"
              />
              <AppButton
                icon="pi pi-download"
                label={t('generic.export')}
                isDisabled={!canExport}
                isSubmit
              />
            </FlexContainer>
          </Form>
        )}
      </Formik>
    </StyledDialog>
  );
};
